import { useQuery } from '@tanstack/react-query';
import { isLoggedInAtom } from 'app/atoms';
import { http } from 'app/lib/axios';
import { userKeys } from 'app/network/keys';
import type { FunctionalUseQueryOptions } from 'app/types/network';
import { useAtomValue } from 'jotai';

/**
 * Mirrors the backend Prisma enum `DailyActionType`.
 * Source of truth: `api/src/users/models/user-daily-stats-response.dto.ts`.
 */
export type DailyActionType =
  'add_avatar' | 'add_bio' | 'publish_offer' | 'publish_request' | 'dismiss_request' | 'send_message' | 'add_favourite';

export interface UserDailyActionDTO {
  id: string;
  userId: string;
  /** ISO date (YYYY-MM-DD) on which the action was performed. */
  date: string;
  action: DailyActionType;
  createdAt: string;
}

export interface UserDailyStatsResponseDTO {
  /** Actions the user has already performed, most-recent first. */
  actions: UserDailyActionDTO[];
  /**
   * Ordered list of action types the user can still perform — first is the highest-priority suggestion.
   * Order depends on the user's `sharerType` preference; `add_avatar` / `add_bio` are excluded once done,
   * and other actions already performed are sorted last.
   */
  actionTypes: DailyActionType[];
}

/**
 * GET /users/daily-stats
 * Returns the actions the current user has performed and the prioritized list of next-best actions.
 */
export function useDailyStats(opts: FunctionalUseQueryOptions<UserDailyStatsResponseDTO> = {}) {
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  return useQuery<UserDailyStatsResponseDTO>({
    queryKey: userKeys.dailyStats(),
    queryFn: () => http.get<UserDailyStatsResponseDTO>('/users/daily-stats').then((res) => res.data),
    ...opts,
    enabled: isLoggedIn && (opts.enabled ?? true),
  });
}
